import { Injectable, inject } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { HttpClientWrapperService, Params } from '../http/http-client-wrapper.service';
import { ApiUrlService } from '../api/api-url.service';
import {
  Event,
  EventBulkInviteResponse,
  EventGuest,
  EventInvitation,
  EventTicketType,
  EventPromoCode,
  EventTable,
  EventGuestRequiredField,
  EventRegistrationField,
  EventRegistrationRequest,
  MyRegistrationResponse,
  TokenRegistrationResponse,
  EventTicketPurchaseWithEvent,
  EventTicketDetail,
  CheckInRequest,
  CheckInResponse,
  TicketPromoPreview,
  TicketPurchaseRequest,
  TicketCheckoutSession,
  ConfirmTicketPurchaseRequest,
  ConfirmTicketPurchaseResponse
} from '../../models/Event';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private readonly apiUrlService = inject(ApiUrlService);
  private get baseUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/events`); }
  public eventsUpdated$: Subject<void> = new Subject<void>();
  private myTicketsCache$?: Observable<EventTicketPurchaseWithEvent[]>;
  private myTicketsCacheUserId?: string | null;

  constructor(private httpClient: HttpClientWrapperService) {}

  // --- Events ---

  getEventsByCharityId(charityId: string): Observable<Event[]> {
    return this.httpClient.get<Event[]>(`${this.baseUrl}?charityId=${charityId}`);
  }

  getEventById(eventId: string): Observable<Event> {
    return this.httpClient.get<Event>(`${this.baseUrl}/${eventId}`);
  }

  getPublicEvent(eventId: string): Observable<Event> {
    return this.httpClient.getGuest<Event>(`${this.baseUrl}/${eventId}/public`);
  }

  createEvent(event: Event, image?: File): Observable<Event> {
    const formData: FormData = new FormData();
    const eventBlob: Blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
    formData.append('event', eventBlob);
    if (image) {
      formData.append('image', image);
    }
    return this.httpClient.post<Event, FormData>(`${this.baseUrl}`, formData);
  }

  updateEvent(eventId: number, event: Event, image?: File): Observable<Event> {
    const formData: FormData = new FormData();
    const eventBlob: Blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
    formData.append('event', eventBlob);
    if (image) {
      formData.append('image', image);
    }
    return this.httpClient.put<Event, FormData>(`${this.baseUrl}/${eventId}`, formData);
  }

  updateEventStatus(eventId: number, status: string): Observable<void> {
    return this.httpClient.put<void, Params>(`${this.baseUrl}/${eventId}/status?status=${status}`, {});
  }

  deleteEvent(eventId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}`);
  }

  // --- Ticket Purchase ---

  purchaseTickets(eventId: number, request: TicketPurchaseRequest): Observable<TicketCheckoutSession> {
    return this.httpClient.postGuest<TicketCheckoutSession, TicketPurchaseRequest>(
      `${this.baseUrl}/${eventId}/tickets/purchase`, request
    );
  }

  /**
   * Validate a promo code against the current ticket selection and get the discount breakdown
   * without starting checkout. Errors carry the same messages the purchase endpoint would return.
   */
  previewPromoCode(eventId: number, request: TicketPurchaseRequest): Observable<TicketPromoPreview> {
    return this.httpClient.postGuest<TicketPromoPreview, TicketPurchaseRequest>(
      `${this.baseUrl}/${eventId}/tickets/promo-preview`, request
    );
  }

  /**
   * Finalize a paid ticket checkout on return from Stripe. Guarantees tickets are created even when
   * the checkout.session.completed Connect webhook is delayed or misconfigured. Idempotent server-side.
   */
  confirmTicketPurchase(eventId: number, checkoutSessionId: string): Observable<ConfirmTicketPurchaseResponse> {
    return this.httpClient.post<ConfirmTicketPurchaseResponse, ConfirmTicketPurchaseRequest>(
      `${this.baseUrl}/${eventId}/tickets/confirm`, { checkoutSessionId }
    );
  }

  // --- Auction Linking ---

  linkAuction(eventId: number, auctionId: number): Observable<void> {
    return this.httpClient.post<void, Params>(`${this.baseUrl}/${eventId}/auctions/${auctionId}`, {});
  }

  unlinkAuction(eventId: number, auctionId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/auctions/${auctionId}`);
  }

  updateAuctionVisibility(eventId: number, auctionId: number, visible: boolean): Observable<void> {
    return this.httpClient.put<void, Params>(
      `${this.baseUrl}/${eventId}/auctions/${auctionId}/visibility?visible=${visible}`, {}
    );
  }

  // --- Guests ---

  getGuests(eventId: number): Observable<EventGuest[]> {
    return this.httpClient.get<EventGuest[]>(`${this.baseUrl}/${eventId}/guests`);
  }

  addGuest(eventId: number, guest: EventGuest): Observable<EventGuest> {
    return this.httpClient.post<EventGuest, EventGuest>(`${this.baseUrl}/${eventId}/guests`, guest);
  }

  updateGuest(eventId: number, guestId: number, guest: EventGuest): Observable<EventGuest> {
    return this.httpClient.put<EventGuest, EventGuest>(`${this.baseUrl}/${eventId}/guests/${guestId}`, guest);
  }

  deleteGuest(eventId: number, guestId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/guests/${guestId}`);
  }

  registerGuestByCharity(eventId: number, guestId: number, request: EventRegistrationRequest): Observable<EventGuest> {
    return this.httpClient.post<EventGuest, EventRegistrationRequest>(
      `${this.baseUrl}/${eventId}/guests/${guestId}/register`, request
    );
  }

  moveGuestToTable(eventId: number, guestId: number, tableId: number): Observable<void> {
    return this.httpClient.put<void, Params>(
      `${this.baseUrl}/${eventId}/guests/${guestId}/table?tableId=${tableId}`, {}
    );
  }

  getGuestRequiredFields(eventId: number): Observable<EventGuestRequiredField[]> {
    return this.httpClient.get<EventGuestRequiredField[]>(`${this.baseUrl}/${eventId}/guests/required-fields`);
  }

  updateGuestRequiredFields(eventId: number, fields: EventGuestRequiredField[]): Observable<void> {
    return this.httpClient.put<void, EventGuestRequiredField[]>(
      `${this.baseUrl}/${eventId}/guests/required-fields`, fields
    );
  }

  // --- Invitations ---

  getInvitations(eventId: number): Observable<EventInvitation[]> {
    return this.httpClient.get<EventInvitation[]>(`${this.baseUrl}/${eventId}/invitations`);
  }

  bulkInvite(eventId: number, emails: string[]): Observable<EventBulkInviteResponse> {
    return this.httpClient.post<EventBulkInviteResponse, { emails: string[] }>(
      `${this.baseUrl}/${eventId}/invitations`, { emails }
    );
  }

  deleteInvitation(eventId: number, invitationId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/invitations/${invitationId}`);
  }

  declineInvitation(eventId: number, token: string): Observable<void> {
    return this.httpClient.putGuest<void, Params>(
      `${this.baseUrl}/${eventId}/invitations/decline?token=${token}`, {}
    );
  }

  // --- Ticket Types ---

  getTicketTypes(eventId: number): Observable<EventTicketType[]> {
    return this.httpClient.get<EventTicketType[]>(`${this.baseUrl}/${eventId}/tickets`);
  }

  createTicketType(eventId: number, ticket: EventTicketType): Observable<EventTicketType> {
    return this.httpClient.post<EventTicketType, EventTicketType>(`${this.baseUrl}/${eventId}/tickets`, ticket);
  }

  updateTicketType(eventId: number, ticketId: number, ticket: EventTicketType): Observable<EventTicketType> {
    return this.httpClient.put<EventTicketType, EventTicketType>(
      `${this.baseUrl}/${eventId}/tickets/${ticketId}`, ticket
    );
  }

  deleteTicketType(eventId: number, ticketId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/tickets/${ticketId}`);
  }

  // --- Promo Codes ---

  getPromoCodes(eventId: number): Observable<EventPromoCode[]> {
    return this.httpClient.get<EventPromoCode[]>(`${this.baseUrl}/${eventId}/promo-codes`);
  }

  createPromoCode(eventId: number, promo: EventPromoCode): Observable<EventPromoCode> {
    return this.httpClient.post<EventPromoCode, EventPromoCode>(`${this.baseUrl}/${eventId}/promo-codes`, promo);
  }

  updatePromoCode(eventId: number, promoId: number, promo: EventPromoCode): Observable<void> {
    return this.httpClient.put<void, EventPromoCode>(`${this.baseUrl}/${eventId}/promo-codes/${promoId}`, promo);
  }

  deletePromoCode(eventId: number, promoId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/promo-codes/${promoId}`);
  }

  // --- Tables ---

  getTables(eventId: number): Observable<EventTable[]> {
    return this.httpClient.get<EventTable[]>(`${this.baseUrl}/${eventId}/tables`);
  }

  createTable(eventId: number, table: EventTable): Observable<EventTable> {
    return this.httpClient.post<EventTable, EventTable>(`${this.baseUrl}/${eventId}/tables`, table);
  }

  updateTable(eventId: number, tableId: number, table: EventTable): Observable<EventTable> {
    return this.httpClient.put<EventTable, EventTable>(`${this.baseUrl}/${eventId}/tables/${tableId}`, table);
  }

  deleteTable(eventId: number, tableId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${eventId}/tables/${tableId}`);
  }

  generateTables(eventId: number, count: number, seatsPerTable: number): Observable<EventTable[]> {
    return this.httpClient.post<EventTable[], Params>(
      `${this.baseUrl}/${eventId}/tables/generate?count=${count}&seatsPerTable=${seatsPerTable}`, {}
    );
  }

  // --- Registration ---

  getMyRegistration(eventId: number): Observable<MyRegistrationResponse> {
    return this.httpClient.get<MyRegistrationResponse>(`${this.baseUrl}/${eventId}/register/me`);
  }

  getRegistrationByToken(eventId: number, token: string): Observable<TokenRegistrationResponse> {
    return this.httpClient.getGuest<TokenRegistrationResponse>(
      `${this.baseUrl}/${eventId}/register/token/${token}`
    );
  }

  registerSelf(eventId: number, data: EventRegistrationRequest): Observable<EventGuest> {
    return this.httpClient.post<EventGuest, EventRegistrationRequest>(
      `${this.baseUrl}/${eventId}/register/me`, data
    );
  }

  registerByToken(eventId: number, token: string, data: EventRegistrationRequest): Observable<EventGuest> {
    return this.httpClient.postGuest<EventGuest, EventRegistrationRequest>(
      `${this.baseUrl}/${eventId}/register/token/${token}`, data
    );
  }

  updateInvitee(eventId: number, guestId: number, data: EventRegistrationRequest): Observable<EventGuest> {
    return this.httpClient.put<EventGuest, EventRegistrationRequest>(
      `${this.baseUrl}/${eventId}/register/${guestId}/invitee`, data
    );
  }

  sendInvite(eventId: number, guestId: number): Observable<void> {
    return this.httpClient.post<void, Params>(
      `${this.baseUrl}/${eventId}/register/${guestId}/send-invite`, {}
    );
  }

  claimGuest(eventId: number, guestId: number): Observable<EventGuest> {
    return this.httpClient.post<EventGuest, Params>(
      `${this.baseUrl}/${eventId}/register/${guestId}/claim`, {}
    );
  }

  getShareLink(eventId: number, guestId: number): Observable<{ link: string }> {
    return this.httpClient.get<{ link: string }>(
      `${this.baseUrl}/${eventId}/register/share-link/${guestId}`
    );
  }

  // --- Registration Fields ---

  getRegistrationFields(eventId: number): Observable<EventRegistrationField[]> {
    return this.httpClient.get<EventRegistrationField[]>(
      `${this.baseUrl}/${eventId}/registration-fields`
    );
  }

  getPublicRegistrationFields(eventId: number): Observable<EventRegistrationField[]> {
    return this.httpClient.getGuest<EventRegistrationField[]>(
      `${this.baseUrl}/${eventId}/registration-fields/public`
    );
  }

  createRegistrationField(eventId: number, field: EventRegistrationField): Observable<EventRegistrationField> {
    return this.httpClient.post<EventRegistrationField, EventRegistrationField>(
      `${this.baseUrl}/${eventId}/registration-fields`, field
    );
  }

  updateRegistrationField(eventId: number, fieldId: number, field: EventRegistrationField): Observable<EventRegistrationField> {
    return this.httpClient.put<EventRegistrationField, EventRegistrationField>(
      `${this.baseUrl}/${eventId}/registration-fields/${fieldId}`, field
    );
  }

  deleteRegistrationField(eventId: number, fieldId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.baseUrl}/${eventId}/registration-fields/${fieldId}`
    );
  }

  reorderRegistrationFields(eventId: number, fields: EventRegistrationField[]): Observable<void> {
    return this.httpClient.put<void, EventRegistrationField[]>(
      `${this.baseUrl}/${eventId}/registration-fields/reorder`, fields
    );
  }

  // --- User Ticket Access (SUN-760) ---

  getMyTickets(): Observable<EventTicketPurchaseWithEvent[]> {
    const currentUserId: string | null = sessionStorage.getItem('userId');
    if (this.myTicketsCacheUserId !== currentUserId) {
      this.invalidateMyTicketsCache();
      this.myTicketsCacheUserId = currentUserId;
    }

    if (!this.myTicketsCache$) {
      const ticketsUrl: string = this.apiUrlService.buildUrl('/api/v1/event-tickets/my-tickets');
      this.myTicketsCacheUserId = currentUserId;
      this.myTicketsCache$ = this.httpClient.get<EventTicketPurchaseWithEvent[]>(ticketsUrl).pipe(
        catchError((error: unknown) => {
          this.myTicketsCache$ = undefined;
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.myTicketsCache$;
  }

  invalidateMyTicketsCache(): void {
    this.myTicketsCache$ = undefined;
    this.myTicketsCacheUserId = undefined;
  }

  getTicketDetail(purchaseId: number): Observable<EventTicketDetail> {
    const ticketUrl: string = this.apiUrlService.buildUrl(`/api/v1/event-tickets/${purchaseId}`);
    return this.httpClient.get<EventTicketDetail>(ticketUrl);
  }

  // --- Charity Check-In (SUN-922) ---

  checkInGuest(charityId: number, ticketCode: string): Observable<CheckInResponse> {
    const checkInUrl: string = this.apiUrlService.buildUrl(`/api/v1/event-tickets/check-in/${charityId}`);
    const request: CheckInRequest = { ticketCode };
    return this.httpClient.post<CheckInResponse, CheckInRequest>(checkInUrl, request);
  }
}
